import { supabase } from "@/integrations/supabase/client";
import type { AymsEventLocation, EapiClient } from "@/lib/eapi-client";
import { eapiTrailerTypeFromKind } from "../../supabase/functions/_shared/models/trailer/trailer-type.ts";
import { resolveTrailerOwningOrg } from "../../supabase/functions/_shared/models/trailer/queries.ts";

export type AymsDirection = "in" | "out";

type AymsSkipReason =
  | "no_eapi_or_tenant"
  | "no_location"
  | "threw";

export type OutboundAymsEventResult = { ok: boolean; reason?: AymsSkipReason };

/**
 * The fixed point AYMS reports for a location: `latitude`/`longitude` when
 * set, else the origin pin (the Locations form writes both from the same
 * input). Returns null when neither is usable.
 */
export function aymsLocationFromRow(
  row: {
    id: string;
    latitude: number | null;
    longitude: number | null;
    origin_latitude: number | null;
    origin_longitude: number | null;
  } | null | undefined,
): AymsEventLocation | null {
  if (!row) return null;
  const lat = row.latitude ?? row.origin_latitude;
  const long = row.longitude ?? row.origin_longitude;
  if (typeof lat !== "number" || typeof long !== "number" || !Number.isFinite(lat) || !Number.isFinite(long)) {
    return null;
  }
  return { id: row.id, lat, long };
}

/**
 * Post a trailer_check_in / trailer_check_out event to EAPI's
 * /v1/ayms/events (ASE-20496).
 *
 * Every gate event is reported, with or without a load. The caller passes the
 * order it already acted on (`orderId`); its `tender_id` becomes
 * `customer_load_id`. Without one the event has no customer_load_id. The order
 * is never looked up by status: check-in delivers it first, so a search for an
 * active order would miss the very load that just arrived.
 *
 * `org_id` is the trailer's owning org: its SCAC names the customer by short
 * code, and that customer's Onramp org in this tenant is the answer
 * (_shared/models/trailer/queries.ts::resolveTrailerOwningOrg). A trailer with
 * no SCAC or no mapped customer still fires, with an empty `org_id`.
 *
 * `tractorId` is what AYMS recorded at the gate; when the caller has none, the
 * order's assigned tractor is used. `trailer_type` comes from the trailer's own
 * `trailer_types.kind`, mapped through the shared EAPI vocabulary
 * (_shared/models/trailer/trailer-type.ts).
 *
 * Best-effort: any failure returns ok=false with a reason and logs a
 * structured warning. Callers treat this strictly as a side effect.
 */
export async function fireAymsEventForTrailer(params: {
  trailerHumanId: string;
  direction: AymsDirection;
  tenantId: string | null | undefined;
  eapiClient: EapiClient | null;
  /** ATMS `locations.id` of the yard where the gate event happened. */
  locationId: string | null | undefined;
  /** The order this gate event belongs to, when the caller knows one. */
  orderId?: string | null;
  /** Tractor as recorded at the gate; falls back to the order's tractor. */
  tractorId?: string | null;
  timestamp?: string;
}): Promise<OutboundAymsEventResult> {
  const { trailerHumanId, direction, tenantId, eapiClient, locationId, orderId } = params;
  const ts = params.timestamp ?? new Date().toISOString();

  if (!eapiClient || !tenantId) {
    console.warn("[ayms] skipped: missing eapiClient or tenantId", {
      hasClient: !!eapiClient,
      tenantId,
      trailerHumanId,
    });
    return { ok: false, reason: "no_eapi_or_tenant" };
  }

  try {
    const { data: locationRow, error: locErr } = locationId
      ? await supabase
          .from("locations")
          .select("id, latitude, longitude, origin_latitude, origin_longitude")
          .eq("id", locationId)
          .maybeSingle()
      : { data: null, error: null };
    if (locErr) throw locErr;
    const location = aymsLocationFromRow(locationRow);
    if (!location) {
      console.warn("[ayms] skipped: location missing or has no coordinates", {
        locationId,
        tenantId,
        trailerHumanId,
        direction,
      });
      return { ok: false, reason: "no_location" };
    }

    const owner = await resolveTrailerOwningOrg(supabase, tenantId, trailerHumanId);
    if (!owner?.org_id) {
      console.warn("[ayms] no org_id (trailer unknown, no SCAC, or SCAC customer has no org in tenant); sending empty", {
        tenantId,
        trailerHumanId,
        trailerFound: !!owner,
        direction,
      });
    }

    const { data: order, error: orderErr } = orderId
      ? await supabase
          .from("orders")
          .select("tender_id, tractors(name)")
          .eq("id", orderId)
          .maybeSingle()
      : { data: null, error: null };
    if (orderErr) throw orderErr;

    const { data: trailer, error: trailerErr } = await supabase
      .from("trailers")
      .select("trailer_types(kind)")
      .eq("tenant_id", tenantId)
      .eq("trailer_id", owner?.trailer_id ?? trailerHumanId)
      .maybeSingle();
    if (trailerErr) throw trailerErr;
    const trailerType = eapiTrailerTypeFromKind(
      (trailer?.trailer_types as { kind?: string } | null | undefined)?.kind,
    );

    const tractorId =
      params.tractorId?.trim() ||
      (order?.tractors as { name?: string } | null | undefined)?.name?.trim() ||
      undefined;

    await eapiClient.submitAymsEvent({
      event_id: crypto.randomUUID(),
      event_type: direction === "in" ? "trailer_check_in" : "trailer_check_out",
      trailer_id: trailerHumanId,
      ...(trailerType ? { trailer_type: trailerType } : {}),
      ...(tractorId ? { tractor_id: tractorId } : {}),
      location,
      ...(order?.tender_id ? { customer_load_id: order.tender_id } : {}),
      org_id: owner?.org_id ?? "",
      timestamp: ts,
    });
    return { ok: true };
  } catch (err) {
    console.error("[ayms] submitAymsEvent threw", {
      trailerHumanId,
      tenantId,
      direction,
      err,
    });
    return { ok: false, reason: "threw" };
  }
}
